import React, { useState, useRef, useEffect } from 'react';
import { Layout, Card, Form, TextArea, Button, Upload, StatusTip, Bubble } from "tea-component";
import ReactMarkdown from 'react-markdown';
import './index.scss';
import { getAuthorization, trim } from '@/utils/utils';
import { getAISummarize } from '@/pages/aigc/service/index';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { txt2File } from '@/pages/home/utils/file';
import { useIntl } from 'umi';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';

interface UploadResultDataProps {
  filename: string;
}

interface UploadResultProps {
  code: number;
  data: UploadResultDataProps;
  msg: string;
}

interface FormErrorInfoProps {
  mapPrompt: string;
  combinePrompt: string;
  fileName: string;
}

const INIT_ERROR_INFO = {
  mapPrompt: '',
  combinePrompt: '',
  fileName: ''
};

const { Content } = Layout;
export default () => {
  const [mapPrompt, setMapPrompt] = useState('');
  const [combinePrompt, setCombinePrompt] = useState('');
  const [fileName, setFileName] = useState('');
  const [aiContent, setAIContent] = useState('');
  const [contentError, setContentError] = useState('');
  const [loading, setLoading] = useState(false);
  const [contentCopied, setContentCopied] = useState(false);
  // 错误信息
  const [errorInfo, setErrorInfo] = useState<FormErrorInfoProps>(INIT_ERROR_INFO);
  const [file, setFile] = useState<any>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [percent, setPercent] = useState<any>(null);
  const xhrRef = useRef(null);

  const { formatMessage } = useIntl();

  function handleStart(file: File, { xhr }) {
    setFile(file);
    setStatus('validating');
    xhrRef.current = xhr;
  }

  function handleProgress({ percent }) {
    setPercent(percent);
  }

  function handleSuccess(result: UploadResultProps) {
    const { code, data, msg } = result || {};
    if (code === 0 && data.filename) {
      setFileName(data.filename);
      setStatus("success");
      setErrorInfo({
        ...errorInfo,
        fileName: ''
      });
    } else {
      setErrorInfo({
        ...errorInfo,
        fileName: msg
      });
    }
  }

  function handleError(error) {
    handleAbort();
    setStatus("error");
    setErrorInfo({
      ...errorInfo,
      fileName: error
    });
  }

  function beforeUpload(file: File, fileList: File[], isAccepted: boolean, reasons) {
    if (!isAccepted) {
      setStatus("error");
      const {message = ''} = reasons[0] || {};
      setErrorInfo({
        ...errorInfo,
        fileName: message
      });
    }
    return isAccepted;
  }

  function handleAbort() {
    if (xhrRef.current) {
      xhrRef.current?.abort();
    }
    setFileName('');
    setFile(null);
    setStatus(null);
    setPercent(null);
  }

  const handleCheckPrompt = () => {
    if (!trim(mapPrompt)) {
      setErrorInfo({
        ...errorInfo,
        mapPrompt: 'dba_report_prompt_error_none'
      });
    } else {
      setErrorInfo({
        ...errorInfo,
        mapPrompt: ''
      });
    }
  };

  function checkForm() : boolean {
    let errorInfo = {
      ...INIT_ERROR_INFO
    };
    let flag = true;
    // if (!trim(mapPrompt)) {
    //   errorInfo = {
    //     ...errorInfo,
    //     mapPrompt: 'dba_report_prompt_error_none'
    //   };
    //   flag =  false;
    // }
    if (!fileName) {
      errorInfo = {
        ...errorInfo,
        fileName: 'dba_report_file_error_none'
      };
      flag =  false;
    }
    setErrorInfo({
      ...errorInfo,
    });
    return flag;
  }

  function handleSubmit() {
    if (!checkForm()) {
      return;
    }
    setAIContent('');
    setContentError('');
    setLoading(true);
    getAISummarize({
      map_prompt: trim(mapPrompt) || formatMessage({id: 'dba_report_prompt_placeholder'}),
      combine_prompt: trim(combinePrompt),
      filename: fileName
    }).then((data) => {
      const { text = '' } = data || {};
      setAIContent(text || 'content empty');
    }, (err) => {
      const {msg = "Loading faild."} = err || {};
      setContentError(msg);
    }).finally(() => {
      setLoading(false);
    });
  }

  const handleClickCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(aiContent).then(() => {
      setContentCopied(true);
      setTimeout(() => {
        setContentCopied(false);
      }, 2000);
    });
  };

  const handleClickDownload = () => {
    txt2File(aiContent);
  };

  useEffect(() => {
    buriedPointPage({
      pageId: '700201',
      pageName: 'reportSummary',
      extInfo: '',
      pagePath: '/reportSummary',
      typeId: "aigc",
    });
  }, []);

  return <>
    <Content.Header
      title={formatMessage({id: 'dba_report_title'})}
    />
    <Content.Body full className={"tools-body"}>
      <Card>
        <Card.Body>
          <Form layout="fixed" fixedLabelWidth={110}>
            <Form.Item label={formatMessage({id: 'dba_report_prompt'})} status={errorInfo.mapPrompt ? 'error' : undefined} message={errorInfo.mapPrompt && formatMessage({id: errorInfo.mapPrompt})}>
              <TextArea
                value={mapPrompt}
                onChange={(val) => {setMapPrompt(val)}}
                // onBlur={handleCheckPrompt}
                className="dba-report-textarea"
                placeholder={formatMessage({id: 'dba_report_prompt_placeholder'})} />
            </Form.Item>
            <Form.Item label={formatMessage({id: 'dba_report_prompt2'})} status={errorInfo.combinePrompt ? 'error' : undefined} message={errorInfo.combinePrompt && formatMessage({id: errorInfo.combinePrompt})}>
              <TextArea
                value={combinePrompt}
                onChange={(val) => {setCombinePrompt(val)}}
                className="dba-report-textarea"
                placeholder={formatMessage({id: 'dba_report_prompt_placeholder'})} />
            </Form.Item>
            <Form.Item label={formatMessage({id: 'dba_report_attachments'})} required>
              <Form.Control
                status={status || (errorInfo.fileName ? 'error' : '')}
                message={errorInfo.fileName && formatMessage({id: errorInfo.fileName, defaultMessage: errorInfo.fileName})}
                extra={formatMessage({id: 'dba_report_upload_message'})}
              >
                <Upload
                  action="/api/v1/ai/upload"
                  accept='text/plain'
                  headers={() => {
                    const token = getAuthorization();
                    return {
                      Authorization: token
                    };
                  }}
                  maxSize={5 * 1024 * 1024}
                  onStart={handleStart}
                  onProgress={handleProgress}
                  onSuccess={handleSuccess}
                  onError={handleError}
                  beforeUpload={beforeUpload}
                >
                  {({ open }) => (
                    <Upload.File
                      filename={file && file.name}
                      percent={percent}
                      button={
                        status === "validating" ? (
                          <Button onClick={handleAbort}>{formatMessage({id: 'aigc_common_upload_cancel'})}</Button>
                        ) : (
                          <>
                            <Button onClick={open}>
                              {status ? formatMessage({id: 'aigc_common_re_upload'}) : formatMessage({id: 'dba_report_upload_button_select'})}
                            </Button>
                            {status && (
                              <Button
                                type="link"
                                style={{ marginLeft: 8 }}
                                onClick={handleAbort}
                              >{formatMessage({id: 'aigc_common_delete'})}</Button>
                            )}
                          </>
                        )
                      }
                    />
                  )}
                </Upload>
              </Form.Control>
            </Form.Item>
          </Form>
          <Form.Action>
            <Button onClick={handleSubmit} type="primary" loading={loading}>{formatMessage({id: 'dba_report_submit'})}</Button>
          </Form.Action>
        </Card.Body>
      </Card>
      {(loading || aiContent || contentError) && <Card>
        <Card.Body
          className='dba-ai-content-container'
          title={formatMessage({id: 'dba_report_result_title'})}
          operation={aiContent ? <div className="dba-card-operation-wrap">
            {contentCopied ? <div className='mr-6'>
              <SvgIcon
                className="overall-conext-icon no-hover"
                type="action-right"
              />
            </div> :
              <Bubble
                arrowPointAtCenter
                content={formatMessage({id: 'dba_report_copy_text'})}
                dark={true}
              >
                <div className='mr-6' onClick={handleClickCopy}>
                  <SvgIcon
                    className="overall-conext-icon"
                    type="datalab-copy"
                  />
                </div>
              </Bubble>}
            <div onClick={handleClickDownload}>
              <SvgIcon
                className="overall-conext-icon"
                type="download"
              />
            </div>
          </div> : undefined}
        >
          {loading ?
            <div className="dba-ai-content-loading-wrap">
              <StatusTip
                // @ts-ignore
                status='loading'
                loadingText={formatMessage({id: 'db_loading'})}
              />
            </div> :
            contentError ?
              <div className="dba-ai-content-loading-wrap">{contentError}</div> :
              <ReactMarkdown
                children={aiContent}
              />
          }
        </Card.Body>
      </Card>}
    </Content.Body>
  </>;
};
